import React, { useState, useEffect, useRef } from 'react';

/**
 * Renders a PDF from a data URI by converting it to a blob URL.
 * Modern browsers block large data URIs in <embed> tags, so we convert
 * to a blob URL and use an <iframe> instead.
 *
 * For regular URLs (non data URIs), renders directly.
 */
const PdfEmbed = ({ src, zoom, style, title }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (!src) {
      setBlobUrl(null);
      return;
    }

    // If it's not a data URI, use it directly
    if (!src.startsWith('data:')) {
      setBlobUrl(src);
      return;
    }

    try {
      const byteString = atob(src.split(',')[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Revoke previous blob URL
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
      blobUrlRef.current = url;
      setBlobUrl(url);
    } catch (e) {
      console.error('[PdfEmbed] Failed to create blob URL:', e);
      setBlobUrl(null);
    }

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [src]);

  if (!blobUrl) return null;

  const zoomSuffix = zoom ? `#zoom=${zoom}` : '#zoom=50';

  return (
    <iframe
      title={title || 'PDF Preview'}
      src={blobUrl + zoomSuffix}
      style={{ border: 'none', ...style }}
    />
  );
};

export default PdfEmbed;
